import { useCallback, useEffect } from 'react';
import { useArtifactNavigation } from '../../../../hooks/useArtifactNavigation';
import { useDrawingTools } from '../../../../hooks/useDrawingTools';
import { useLocalizedDate } from '../../../../hooks/useLocalizedDate';
import { useGeoMarkupStore } from '../../../../store';
import { Task } from '../../../../types';
import type { UseTaskWizardFormReturn } from '../utils/mantineFormSchema';
import { detectUnsavedChanges } from '../utils/unsavedChangesDetector';

interface UseWizardNavigationProps {
  form: UseTaskWizardFormReturn['form'];
  editingTask?: Task | null;
  goToStep: UseTaskWizardFormReturn['goToStep'];
  initialLocationValue: string | null;
}

export const useWizardNavigation = ({
  form,
  editingTask,
  goToStep,
  initialLocationValue,
}: UseWizardNavigationProps) => {
  const { closeOverlay } = useArtifactNavigation();
  const { currentGeometries } = useDrawingTools(); // Get geometries from Zustand for unsaved changes detection
  const { formatForInput } = useLocalizedDate();
  const setFormIsDirty = useGeoMarkupStore(
    (state) => state.tasks.setFormIsDirty
  );
  const requestFormGuard = useGeoMarkupStore(
    (state) => state.formConflict.request
  );

  // Check for unsaved changes using centralized utility
  const hasUnsavedChanges = useCallback(() => {
    return detectUnsavedChanges(
      form.values,
      editingTask,
      currentGeometries,
      formatForInput,
      initialLocationValue
    );
  }, [
    form.values,
    editingTask,
    currentGeometries,
    formatForInput,
    initialLocationValue,
  ]);

  // Mirror dirty state into the store so the global unsaved-changes guard can
  // decide between silent close and the confirmation prompt.
  useEffect(() => {
    setFormIsDirty(hasUnsavedChanges());
  }, [hasUnsavedChanges, setFormIsDirty]);

  // Direct close (no prompt) — used post-save and as the guard's continuation.
  const forceClose = useCallback(() => {
    form.reset();
    closeOverlay();
  }, [form, closeOverlay]);

  // User-initiated close routes through the global guard: a dirty form prompts
  // (single dialog in AppShell) before discarding; a clean form closes silently.
  const handleClose = useCallback(() => {
    requestFormGuard(forceClose);
  }, [requestFormGuard, forceClose]);

  // Stable identity so the memoized section renderer doesn't re-render on every
  // parent render. Free navigation — jump to any section tab.
  const handleStepClick = useCallback(
    (step: number) => {
      goToStep(step);
    },
    [goToStep]
  );

  return {
    handleClose,
    forceClose,
    handleStepClick,
  };
};
